/**
 * DangoteAssetDetail — Asset-level page styled EXACTLY like Oko-Oba Climate Snapshot.
 * Route: /corporate/asset/:asset_id
 *
 * Architecture: Reads asset calculation from DangoteContext consolidated data.
 * Structure mirrors GenericClimateSnapshot.js:
 *   A. Emission Structure (Scope 1/2/3 — same layout as Oko-Oba)
 *   B. Circularity Metrics
 *   C. Carbon Pathway Screening
 *   D. Financial Model
 */
import React, { useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDangote } from "@/context/DangoteContext";
import { PageHeader, SectionCard, MetricCard } from "@/components/ds";
import { RefreshCw, ArrowLeft, Activity, TrendingDown, ChevronRight, Shield,
         CheckCircle2, XCircle, AlertTriangle, Recycle } from "lucide-react";
import client from "@/api/client";

const ASSET_META = {
  "AST-OBJ": { name: "Obajana Cement Plant",           type: "cement",    bu: "Dangote Cement",            benchmarkKey: "tco2e_per_t_cement",  benchmarkVal: 0.65, benchmarkLabel: "tCO₂e/t cement" },
  "AST-IBS": { name: "Ibese Cement Plant",              type: "cement",    bu: "Dangote Cement",            benchmarkKey: "tco2e_per_t_cement",  benchmarkVal: 0.65, benchmarkLabel: "tCO₂e/t cement" },
  "AST-GBK": { name: "Gboko Cement Plant",              type: "cement",    bu: "Dangote Cement",            benchmarkKey: "tco2e_per_t_cement",  benchmarkVal: 0.65, benchmarkLabel: "tCO₂e/t cement" },
  "AST-DRF": { name: "Dangote Petroleum Refinery",      type: "refinery",  bu: "Dangote Refinery",          benchmarkKey: "tco2e_per_bbl",       benchmarkVal: 0.25, benchmarkLabel: "tCO₂e/bbl" },
  "AST-DFT": { name: "Dangote Fertilizer Urea Plant",   type: "fertilizer",bu: "Dangote Fertilizer",        benchmarkKey: "tco2e_per_t_urea",    benchmarkVal: 0.45, benchmarkLabel: "tCO₂e/t urea" },
  "AST-DSG": { name: "Apapa Sugar Refinery",            type: "sugar",     bu: "Dangote Sugar",             benchmarkKey: "tco2e_per_t_sugar",   benchmarkVal: 0.20, benchmarkLabel: "tCO₂e/t sugar" },
  "AST-DLG": { name: "Dangote Fleet Operations HQ",     type: "logistics", bu: "Dangote Logistics",         benchmarkKey: "tco2e_per_1000km",    benchmarkVal: 0.60, benchmarkLabel: "tCO₂e/1000km" },
};

const PATHWAY_STATUS = {
  vcm:      { label: "VCM",      eligible_color: "bg-teal-100 text-teal-700 border-teal-200",     ineligible_color: "bg-slate-100 text-slate-500 border-slate-200" },
  art6:     { label: "Art. 6",   eligible_color: "bg-blue-100 text-blue-700 border-blue-200",     ineligible_color: "bg-slate-100 text-slate-500 border-slate-200" },
  internal: { label: "Internal", eligible_color: "bg-amber-100 text-amber-700 border-amber-200", ineligible_color: "bg-slate-100 text-slate-500 border-slate-200" },
};

export default function DangoteAssetDetail() {
  const { asset_id } = useParams();
  const navigate     = useNavigate();
  const { consolidated, selectedPeriod, period, loading } = useDangote();

  const [recalculating, setRecalculating] = useState(false);

  // Find this asset's calculation in consolidated data
  const assetCalc = useMemo(() => {
    if (!consolidated) return null;
    for (const bu of (consolidated.by_bu || [])) {
      const found = (bu.assets || []).find(a => a.asset_id === asset_id);
      if (found) return { ...found, bu_name: bu.bu_name, sector_type: bu.sector_type };
    }
    return null;
  }, [consolidated, asset_id]);

  // Full calculation detail (from direct API call for detail view)
  const [detail, setDetail] = React.useState(null);
  const [detailLoading, setDetailLoading] = React.useState(true);
  React.useEffect(() => {
    if (!selectedPeriod) return;
    setDetailLoading(true);
    client.get(`/dangote/assets/${asset_id}?period_id=${selectedPeriod}`)
      .then(r => { setDetail(r.data); setDetailLoading(false); })
      .catch(() => setDetailLoading(false));
  }, [asset_id, selectedPeriod]);

  const handleRecalc = async () => {
    if (!selectedPeriod) return;
    setRecalculating(true);
    try {
      await client.post(`/dangote/calculate/${asset_id}/${selectedPeriod}`);
      client.get(`/dangote/assets/${asset_id}?period_id=${selectedPeriod}`)
        .then(r => setDetail(r.data));
    } catch(e) { console.error(e); }
    setRecalculating(false);
  };

  const meta  = ASSET_META[asset_id] || { name: asset_id, type: "cement", bu: "", benchmarkVal: 0 };
  const calc  = detail?.calculation || {};
  const circ  = calc.circularity  || {};
  const intens= calc.intensity    || {};
  const opps  = detail?.opportunities || [];
  const total = calc.total_tco2e || 0;
  const s1    = calc.scope1?.total_tco2e || 0;
  const s2    = calc.scope2?.electricity_tco2e || 0;
  const s3    = calc.scope3?.total_tco2e || 0;
  const maxBar = Math.max(s1, s2, s3, 1);

  // ── Output Tiers (same as Oko-Oba) ────────────────────────────────────
  const coreReduction = s1;   // Scope 1 = core (like methane avoidance in Oko-Oba)
  const extReduction  = total; // Scope 1+2+3 = extended
  const oppPotential  = opps.reduce((sum,o) => sum + (o.reduction_tco2e_yr||0), 0);

  // ── Benchmark comparison ───────────────────────────────────────────────
  const currentIntensity = intens[meta.benchmarkKey] || 0;
  const benchmarkGap     = currentIntensity > meta.benchmarkVal
    ? { label: `${((currentIntensity - meta.benchmarkVal)/meta.benchmarkVal*100).toFixed(0)}% above benchmark`, color: "text-red-600" }
    : { label: `${((meta.benchmarkVal - currentIntensity)/meta.benchmarkVal*100).toFixed(0)}% below benchmark`, color: "text-emerald-700" };

  if (loading && !detail) return (
    <div className="p-6 space-y-4">
      {[1,2,3].map(i=><div key={i} className="h-16 bg-slate-100 rounded-xl animate-pulse"/>)}
    </div>
  );

  return (
    <div className="p-6 lg:p-8 space-y-6">
      {/* ── Header — same as Oko-Oba Climate Snapshot ────────────────────── */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-700 mb-2">
            <ArrowLeft size={12} /> Back
          </button>
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-0.5">
            {meta.bu} · {meta.type}
          </p>
          <PageHeader title={meta.name} />
          <p className="text-xs text-slate-400 mt-0.5">
            {period?.label} · Asset: {asset_id} · IPCC 2006 Guidelines
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {total > 0 && (
            <span className="text-xs font-bold px-2 py-0.5 rounded-full border bg-teal-100 text-teal-700 border-teal-200">
              Calculated
            </span>
          )}
          <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${
            total > 0 ? "bg-amber-100 text-amber-700 border-amber-200" : "bg-slate-100 text-slate-500 border-slate-200"
          }`}>Verification: Not Initiated</span>
          <button onClick={handleRecalc} disabled={recalculating}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-teal-600 text-white text-xs font-bold rounded-xl disabled:opacity-50">
            {recalculating ? <RefreshCw size={10} className="animate-spin"/> : <Activity size={10}/>} Recalculate
          </button>
        </div>
      </div>

      {/* KPI strip — same as Oko-Oba KPI strip */}
      {total > 0 && (
        <div className="grid grid-cols-4 gap-4">
          <MetricCard label="Total tCO₂e" value={Number(total).toLocaleString()} unit="tCO₂e/month" color="text-slate-900" />
          <MetricCard label="Scope 1" value={Number(s1).toLocaleString()} unit="tCO₂e" color="text-red-700" />
          <MetricCard label="Scope 2" value={Number(s2).toLocaleString()} unit="tCO₂e" color="text-orange-600" />
          <MetricCard label="Intensity" value={currentIntensity?.toFixed ? currentIntensity.toFixed(3) : "—"}
            unit={meta.benchmarkLabel} color={currentIntensity > meta.benchmarkVal ? "text-red-700" : "text-emerald-700"} />
        </div>
      )}

      {total === 0 ? (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-8 text-center">
          <Activity size={20} className="text-amber-400 mx-auto mb-2" />
          <p className="text-sm font-bold text-amber-800">No calculation data for this period</p>
          <p className="text-xs text-amber-600 mt-1">Upload data via Monthly Reporting or click Recalculate.</p>
          <button onClick={() => navigate("/corporate/reporting")}
            className="mt-3 px-4 py-2 bg-teal-600 text-white text-xs font-bold rounded-xl">
            Go to Monthly Reporting
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Left: A. Emission Structure (same as Oko-Oba ClimateSnapshot) */}
          <div className="lg:col-span-2 space-y-5">

            <SectionCard>
              <div className="flex items-center justify-between mb-4">
                <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold">A. Emission Structure — Scope by Scope</p>
                <span className="text-xs text-slate-400 italic">{period?.label}</span>
              </div>

              {/* Scope 1 — like Oko-Oba Module A */}
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-2.5">
                  <span className="text-xs font-bold bg-red-100 text-red-700 px-2 py-0.5 rounded">MODULE A</span>
                  <p className="text-xs font-bold text-slate-600 uppercase tracking-wide">Scope 1 — Combustion + Process</p>
                  <span className="text-xs text-slate-400 italic ml-auto">IPCC 2006 Calculated</span>
                </div>
                {[
                  { label: "Scope 1 Combustion", value: calc.scope1?.combustion_tco2e, color: "bg-red-400" },
                  { label: "Scope 1 Process",    value: calc.scope1?.process_tco2e,    color: "bg-red-300" },
                  { label: "Scope 1 Total",       value: s1,                            color: "bg-red-600" },
                ].filter(x => x.value > 0).map(({ label, value, color }) => {
                  const pct = maxBar > 0 ? Math.min((value/maxBar)*100, 100) : 0;
                  return (
                    <div key={label} className="mb-2.5">
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-slate-600">{label}</span>
                        <span className="font-bold text-slate-900">{Number(value).toLocaleString()} tCO₂e</span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${color}`} style={{width:`${pct}%`}} />
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Scope 2 — like Oko-Oba Module B */}
              {s2 > 0 && (
                <div className="mb-4 pt-3 border-t border-slate-100">
                  <div className="flex items-center gap-2 mb-2.5">
                    <span className="text-xs font-bold bg-orange-100 text-orange-700 px-2 py-0.5 rounded">MODULE B</span>
                    <p className="text-xs font-bold text-slate-600 uppercase tracking-wide">Scope 2 — Electricity</p>
                    <span className="text-xs text-amber-600 italic ml-1">IEA Nigeria 0.43 kgCO₂/kWh</span>
                  </div>
                  <div className="mb-2">
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-slate-600">Electricity consumption</span>
                      <span className="font-bold text-slate-900">{Number(s2).toLocaleString()} tCO₂e</span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full bg-orange-400" style={{width:`${Math.min((s2/maxBar)*100,100)}%`}} />
                    </div>
                  </div>
                </div>
              )}

              {/* Scope 3 */}
              {s3 > 0 && (
                <div className="pt-3 border-t border-slate-100">
                  <div className="flex items-center gap-2 mb-2.5">
                    <span className="text-xs font-bold bg-purple-100 text-purple-700 px-2 py-0.5 rounded">MODULE C</span>
                    <p className="text-xs font-bold text-slate-600 uppercase tracking-wide">Scope 3 — Transport</p>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-600">Logistics / transport</span>
                    <span className="font-bold text-slate-900">{Number(s3).toLocaleString()} tCO₂e</span>
                  </div>
                </div>
              )}

              {/* Output Tiers — same as Oko-Oba */}
              <div className="mt-4 pt-4 border-t-2 border-slate-200">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3">Output Tiers</p>
                <div className="space-y-2">
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold bg-emerald-600 text-white px-2 py-0.5 rounded">CORE</span>
                      <div>
                        <p className="text-xs font-bold text-emerald-800">Scope 1 — Process Emissions</p>
                        <p className="text-xs text-emerald-600">Primary combustion + process boundary</p>
                      </div>
                    </div>
                    <p className="text-sm font-bold text-emerald-800">{Number(s1).toLocaleString()} tCO₂e/month</p>
                  </div>
                  <div className="bg-teal-50 border border-teal-200 rounded-xl px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold bg-teal-600 text-white px-2 py-0.5 rounded">EXTENDED</span>
                      <div>
                        <p className="text-xs font-bold text-teal-800">Scope 1+2+3 — Total GHG</p>
                        <p className="text-xs text-teal-600">{Number(total/1000).toFixed(1)}K tCO₂e/month · {(Number(total)*12/1000).toFixed(0)}K tCO₂e/yr</p>
                      </div>
                    </div>
                    <p className="text-sm font-bold text-teal-800">{Number(total).toLocaleString()} tCO₂e/month</p>
                  </div>
                  {oppPotential > 0 && (
                    <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold bg-blue-600 text-white px-2 py-0.5 rounded">PIPELINE</span>
                        <div>
                          <p className="text-xs font-bold text-blue-800">Reduction Opportunities</p>
                          <p className="text-xs text-blue-600">If implemented</p>
                        </div>
                      </div>
                      <p className="text-sm font-bold text-blue-800">{Number(oppPotential).toLocaleString()} tCO₂e/yr</p>
                    </div>
                  )}
                </div>
              </div>
            </SectionCard>

            {/* B. Circularity — same as Oko-Oba Section B */}
            {Object.keys(circ).length > 0 && meta.type === "cement" && (
              <div className={`border rounded-xl p-5 ${
                circ.afr_substitution_pct > 10 ? "bg-teal-50 border-teal-200" : "bg-amber-50 border-amber-200"
              }`} data-testid="circularity-panel">
                <div className="flex items-center gap-2 mb-3">
                  <Recycle size={14} />
                  <p className="text-xs font-bold uppercase tracking-wider">B. Circularity Layer</p>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                  {[
                    {l:"AFR Substitution", v:`${circ.afr_substitution_pct?.toFixed(1)}%`, ok:circ.afr_substitution_pct>10, note:"Target: >15%"},
                    {l:"Clinker/Cement Ratio", v:`${circ.clinker_to_cement_ratio?.toFixed(3)}`, ok:circ.clinker_to_cement_ratio<0.75, note:"Target: <0.75"},
                    {l:"Alt. Raw Materials", v:`${circ.alt_raw_materials_pct?.toFixed(1)}%`, ok:circ.alt_raw_materials_pct>5, note:"Target: >10%"},
                    {l:"Fly Ash Use", v:`${circ.fly_ash_use_pct?.toFixed(1)}%`, ok:circ.fly_ash_use_pct>3, note:""},
                    {l:"Clinker Production", v:`${Number(circ.clinker_production_t||0).toLocaleString()} t`, ok:true, note:""},
                    {l:"AFR Consumed", v:`${Number(circ.afr_t_consumed||0).toLocaleString()} t`, ok:true, note:""},
                  ].map(({l,v,ok,note}) => (
                    <div key={l} className="bg-white/70 rounded-xl p-3">
                      <div className="flex items-center gap-1 mb-0.5">
                        <div className={`w-1.5 h-1.5 rounded-full ${ok?"bg-teal-500":"bg-amber-400"}`} />
                        <p className="text-xs text-slate-400">{l}</p>
                      </div>
                      <p className={`text-sm font-bold ${ok?"text-teal-800":"text-amber-800"}`}>{v || "—"}</p>
                      {note && <p className="text-[9px] text-slate-400 italic">{note}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right: C. Carbon Pathway + Financial + Methodology */}
          <div className="space-y-4">
            {/* Benchmark */}
            {currentIntensity > 0 && (
              <SectionCard>
                <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-3">Intensity Benchmark</p>
                <div className="text-center py-2">
                  <p className="text-2xl font-bold text-slate-900">{currentIntensity.toFixed(3)}</p>
                  <p className="text-xs text-slate-400">{meta.benchmarkLabel}</p>
                  <p className={`text-xs font-bold mt-1 ${benchmarkGap.color}`}>{benchmarkGap.label}</p>
                  <p className="text-[9px] text-slate-400 mt-0.5">vs global benchmark {meta.benchmarkVal}</p>
                </div>
                <div className="mt-3 relative h-3 bg-slate-100 rounded-full overflow-hidden">
                  <div className="absolute left-0 h-full bg-red-400 rounded-l-full"
                    style={{width:`${Math.min(currentIntensity/Math.max(currentIntensity*1.2,meta.benchmarkVal*1.5)*100,100)}%`}} />
                  <div className="absolute h-full w-0.5 bg-emerald-600"
                    style={{left:`${meta.benchmarkVal/Math.max(currentIntensity*1.2,meta.benchmarkVal*1.5)*100}%`}} />
                </div>
                <div className="flex justify-between text-[9px] text-slate-400 mt-1">
                  <span>Current: {currentIntensity.toFixed(3)}</span>
                  <span className="text-emerald-600">Benchmark: {meta.benchmarkVal}</span>
                </div>
              </SectionCard>
            )}

            {/* C. Carbon Pathway Screening */}
            {opps.length > 0 && (
              <SectionCard>
                <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-3">
                  C. Carbon Pathway — {opps.length} Opportunities
                </p>
                {opps.slice(0,4).map(o => {
                  const vcm = o.vcm_eligible;
                  const a6  = o.art6_eligible;
                  return (
                    <div key={o.opp_id} className="mb-3 last:mb-0 bg-slate-50 border border-slate-100 rounded-xl p-3">
                      <div className="flex items-start justify-between gap-2 mb-1.5">
                        <p className="text-xs font-semibold text-slate-900">{o.name}</p>
                        <div className="flex gap-1 flex-shrink-0">
                          {vcm && <span className="text-[9px] font-bold bg-teal-100 text-teal-700 px-1.5 py-0.5 rounded border border-teal-200">VCM</span>}
                          {a6  && <span className="text-[9px] font-bold bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded border border-blue-200">Art.6</span>}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-1 text-xs">
                        <div><span className="text-slate-400">Reduction: </span><span className="text-emerald-700 font-bold">{Number(o.reduction_tco2e_yr).toLocaleString()} tCO₂e/yr</span></div>
                        <div><span className="text-slate-400">IRR: </span><span className="text-teal-700 font-bold">{o.irr_pct}%</span></div>
                      </div>
                    </div>
                  );
                })}
                <button onClick={() => navigate("/corporate/carbon-strategy")}
                  className="w-full mt-2 py-2 bg-[#0F172A] text-white text-xs font-bold rounded-xl flex items-center justify-center gap-1">
                  Full Carbon Strategy <ChevronRight size={11} />
                </button>
              </SectionCard>
            )}

            {/* Methodology */}
            <SectionCard>
              <p className="text-xs text-slate-400 font-semibold mb-2">C. Integrity Layer</p>
              <div className="space-y-1 text-xs">
                {[
                  ["Calculation Basis", "IPCC 2006 Guidelines"],
                  ["Protocol",          "DCI-CORPORATE-V1.0"],
                  ["Crediting Pathway", "Not Assigned"],
                  ["Verification",      "Not Initiated"],
                ].map(([l,v]) => (
                  <div key={l} className="flex justify-between py-1 border-b border-slate-50 last:border-0">
                    <span className="text-slate-400">{l}</span>
                    <span className="font-semibold text-slate-700">{v}</span>
                  </div>
                ))}
              </div>
            </SectionCard>
          </div>
        </div>
      )}
    </div>
  );
}
